'use client';

import { c as _c } from "react/compiler-runtime";
import { ChartTooltipPanel, FloatingTooltip } from '@/domains/pages/shared/chart-tooltip';
import { AIFI_DATA_NUMBER_CLASS } from '@/domains/pages/ai-proficiency/typography';
import { formatUserCount } from '@/domains/pages/ai-proficiency/utils/format-users';
import { formatScore } from '@/utils/format.util';
import type { LucideIcon } from 'lucide-react';
import { useCallback, useRef, useState } from 'react';
const DEFAULT_SIZE_PX = 56;
const STROKE_PX = 4;
/** Thinner arc on the compact ring, so a small circle doesn't read as a blob. */
const COMPACT_STROKE_PX = 3;

/** Neutral ring behind the arc. */
const TRACK_COLOR = 'rgba(255, 255, 255, 0.08)';
type LevelShareDonutProps = {
  /** Share of the population at this level, 0-100. */
  sharePercent: number;
  color: string;
  label: string;
  /** Headcount at this level, shown inside the ring and in the hover panel. */
  userCount: number;
  /** Icon shown next to the headcount, saying what the figure counts. */
  CenterIcon?: LucideIcon;
  /** Outer diameter; the centre figure only fits from the default size up. */
  sizePx?: number;
  /** What the share is a share of, for the hover panel. */
  shareOf?: string;
};

/** The level's share drawn as a ring, with the headcount inside it. */
export function LevelShareDonut(t0) {
  const $ = _c(56);
  const {
    sharePercent,
    color,
    label,
    userCount,
    CenterIcon,
    sizePx: t1,
    shareOf: t2
  } = t0;
  const sizePx = t1 === undefined ? DEFAULT_SIZE_PX : t1;
  const shareOf = t2 === undefined ? "workforce" : t2;
  const clamped = Math.max(0, Math.min(100, sharePercent));
  const strokePx = sizePx >= DEFAULT_SIZE_PX ? STROKE_PX : COMPACT_STROKE_PX;
  const center = sizePx / 2;
  const radius = (sizePx - strokePx) / 2;
  const circumference = 2 * Math.PI * radius;
  const showCenterFigure = sizePx >= DEFAULT_SIZE_PX;
  const ringRef = useRef(null);
  const [tooltipPos, setTooltipPos] = useState(null);
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      const rect = ringRef.current?.getBoundingClientRect();
      if (!rect) {
        return;
      }
      setTooltipPos({
        x: rect.left + rect.width / 2,
        y: rect.top
      });
    };
    $[0] = t3;
  } else {
    t3 = $[0];
  }
  const showTooltip = t3;
  let t4;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => setTooltipPos(null);
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  const hideTooltip = t4;
  let t5;
  if ($[2] !== sizePx) {
    t5 = {
      width: sizePx,
      height: sizePx
    };
    $[2] = sizePx;
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  let t6;
  if ($[4] !== clamped) {
    t6 = formatScore(clamped, {
      decimals: 1
    });
    $[4] = clamped;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  let t7;
  if ($[6] !== userCount) {
    t7 = formatUserCount(userCount);
    $[6] = userCount;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  const t8 = `${label}: ${t6}% of ${shareOf}, ${t7} users`;
  const t9 = `0 0 ${sizePx} ${sizePx}`;
  const t10 = `rotate(-90 ${center} ${center})`;
  let t11;
  if ($[8] !== center || $[9] !== radius || $[10] !== strokePx) {
    t11 = <circle cx={center} cy={center} r={radius} fill="none" stroke={TRACK_COLOR} strokeWidth={strokePx} />;
    $[8] = center;
    $[9] = radius;
    $[10] = strokePx;
    $[11] = t11;
  } else {
    t11 = $[11];
  }
  const t12 = `${clamped / 100 * circumference} ${circumference}`;
  let t13;
  if ($[12] !== center || $[13] !== color || $[14] !== radius || $[15] !== strokePx || $[16] !== t12) {
    t13 = <circle cx={center} cy={center} r={radius} fill="none" stroke={color} strokeWidth={strokePx} strokeLinecap="round" strokeDasharray={t12} />;
    $[12] = center;
    $[13] = color;
    $[14] = radius;
    $[15] = strokePx;
    $[16] = t12;
    $[17] = t13;
  } else {
    t13 = $[17];
  }
  let t14;
  if ($[18] !== t10 || $[19] !== t11 || $[20] !== t13) {
    t14 = <g transform={t10}>{t11}{t13}</g>;
    $[18] = t10;
    $[19] = t11;
    $[20] = t13;
    $[21] = t14;
  } else {
    t14 = $[21];
  }
  let t15;
  if ($[22] !== sizePx || $[23] !== t14 || $[24] !== t9) {
    t15 = <svg width={sizePx} height={sizePx} viewBox={t9} aria-hidden={true}>{t14}</svg>;
    $[22] = sizePx;
    $[23] = t14;
    $[24] = t9;
    $[25] = t15;
  } else {
    t15 = $[25];
  }
  let t16;
  if ($[26] !== CenterIcon || $[27] !== showCenterFigure || $[28] !== userCount) {
    t16 = showCenterFigure ? <span className="absolute inset-0 flex items-center justify-center gap-1 text-muted-foreground">{CenterIcon ? <CenterIcon className="size-3 shrink-0" strokeWidth={2.25} aria-hidden={true} /> : null}<span className={`text-[11px] ${AIFI_DATA_NUMBER_CLASS}`}>{formatUserCount(userCount)}</span></span> : null;
    $[26] = CenterIcon;
    $[27] = showCenterFigure;
    $[28] = userCount;
    $[29] = t16;
  } else {
    t16 = $[29];
  }
  const t17 = tooltipPos != null;
  const t18 = tooltipPos?.x ?? 0;
  const t19 = tooltipPos?.y ?? 0;
  let t20;
  if ($[30] !== clamped) {
    t20 = formatScore(clamped, {
      decimals: 1
    });
    $[30] = clamped;
    $[31] = t20;
  } else {
    t20 = $[31];
  }
  const t21 = `${t20}% of ${shareOf}`;
  let t22;
  if ($[32] !== color) {
    t22 = {
      color
    };
    $[32] = color;
    $[33] = t22;
  } else {
    t22 = $[33];
  }
  let t23;
  if ($[34] !== label || $[35] !== t21 || $[36] !== t22) {
    t23 = {
      label,
      value: t21,
      valueStyle: t22
    };
    $[34] = label;
    $[35] = t21;
    $[36] = t22;
    $[37] = t23;
  } else {
    t23 = $[37];
  }
  let t24;
  if ($[38] !== userCount) {
    t24 = formatUserCount(userCount);
    $[38] = userCount;
    $[39] = t24;
  } else {
    t24 = $[39];
  }
  let t25;
  if ($[40] !== t24) {
    t25 = {
      label: "Users",
      value: t24
    };
    $[40] = t24;
    $[41] = t25;
  } else {
    t25 = $[41];
  }
  let t26;
  if ($[42] !== t23 || $[43] !== t25) {
    t26 = <ChartTooltipPanel rows={[t23, t25]} />;
    $[42] = t23;
    $[43] = t25;
    $[44] = t26;
  } else {
    t26 = $[44];
  }
  let t27;
  if ($[45] !== t17 || $[46] !== t18 || $[47] !== t19 || $[48] !== t26) {
    t27 = <FloatingTooltip open={t17} x={t18} y={t19}>{t26}</FloatingTooltip>;
    $[45] = t17;
    $[46] = t18;
    $[47] = t19;
    $[48] = t26;
    $[49] = t27;
  } else {
    t27 = $[49];
  }
  let t28;
  if ($[50] !== t15 || $[51] !== t16 || $[52] !== t27 || $[53] !== t5 || $[54] !== t8) {
    t28 = <div ref={ringRef} className="relative shrink-0 cursor-pointer" style={t5} role="img" aria-label={t8} onMouseEnter={showTooltip} onMouseLeave={hideTooltip}>{t15}{t16}{t27}</div>;
    $[50] = t15;
    $[51] = t16;
    $[52] = t27;
    $[53] = t5;
    $[54] = t8;
    $[55] = t28;
  } else {
    t28 = $[55];
  }
  return t28;
}